import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React from 'react';
import { Link } from 'react-router-dom';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { formatDate, numeralFormat } from 'src/utils/misc';
import GenericTable, { GTCheckBox, GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { IncompleteStateMarkerNew, RankIndicator, TextOverflowTooltip } from 'src/common';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TableIds } from 'src/api/types/misc';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { ControlWithScenarios } from '../types';
import _ from 'lodash';

type Scenario = ControlWithScenarios['scenarios'][number]['scenario'];
type Data = { scenario: Scenario; score: number; isLinked: boolean };

interface ControlTableExpandContentProps {
  data: Data[];
  assessment: { id: string; name: string };
  minScore: number;
  maxScore: number;
  selectedControls: Record<string, Record<string, boolean>>;
  setFieldValue: (field: string, value: any) => void;
  controlId: string;
}

const defaultColumns = ['status_scen', 'name', 'expected_loss', '_score'];
const internalIds = [GTInternalIds.expander, GTInternalIds.checker, GTInternalIds.tdm];

export const ControlTableExpandContent: React.FC<ControlTableExpandContentProps> = ({
  data,
  assessment,
  minScore,
  maxScore,
  selectedControls,
  setFieldValue,
  controlId,
}) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.wizardRecomendedControlsExpandContent}-columns`,
    defaultColumns,
  );

  const columns: ColumnDef<Data>[] = [
    {
      id: GTInternalIds.checker,
      header: () => null,
      cell: ({ row }) => {
        const scenarioId = row.original.scenario.id;
        const isChecked = _.get(selectedControls, [controlId, scenarioId], false);

        return (
          <GTCheckBox
            isChecked={isChecked || row.original.isLinked}
            disabled={row.original.isLinked}
            onClick={() => {
              const updatedSelectedControls = _.set(_.cloneDeep(selectedControls), [controlId, scenarioId], !isChecked);

              setFieldValue('selectedControls', updatedSelectedControls);
            }}
          />
        );
      },
      size: 1,
      maxSize: 35,
      enableSorting: false,
    },
    {
      id: 'assessment_name',
      header: 'Assessment',
      cell: () => assessment.name,
      sortingFn: sortingFns.text,
    },
    {
      id: 'ux_id',
      header: 'Id',
      accessorKey: 'scenario.id',
      cell: (info) => info.getValue(),
    },
    {
      id: 'status_scen',
      header: 'Status',
      accessorFn: (row) => getScenarioStatusName(row.scenario.status),
      cell: (info) => info.getValue(),
    },
    {
      id: 'name',
      header: 'Scenario',
      accessorFn: (row) => row.scenario.name,
      cell: (info) => (
        <TextOverflowTooltip>
          <Link
            className="table-link"
            to={`/risk-assessments/${assessment.id}/scenario/${info.row.original.scenario.id}`}
            onClick={(e) => e.stopPropagation()}
            target="_blank"
          >
            {info.getValue() as string}
          </Link>
        </TextOverflowTooltip>
      ),
      sortUndefined: 'last',
      sortingFn: sortingFns.text,
    },
    {
      id: 'source',
      header: 'Source',
      accessorFn: (row) => getRiskSourcesName(row.scenario.source),
      cell: (info) => info.getValue(),
    },
    {
      id: 'event',
      header: 'Event',
      accessorFn: (row) => getRiskEventsName(row.scenario.event),
      cell: (info) => info.getValue(),
    },
    {
      id: 'consequence',
      header: 'Consequence',
      accessorFn: (row) => getRiskConsequencesName(row.scenario.consequence),
      cell: (info) => info.getValue(),
    },
    {
      id: 'risk_owner',
      header: 'Risk Owner',
      accessorFn: (row) => row.scenario.ownerName,
      cell: (info) => info.getValue(),
      sortingFn: sortingFns.text,
    },
    {
      id: 'expected_loss',
      header: 'Expected Loss',
      accessorFn: (row) => row.scenario.liveRisk?.expectedLoss,
      cell: (info) => numeralFormat(info.getValue() as number),
    },
    {
      id: 'risk_identified_date',
      header: 'Identified Date',
      accessorFn: (row) => row.scenario.riskIdentifiedDate || '',
      cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
    },
    {
      id: '_score',
      header: 'Score',
      accessorFn: (row) => row.score || 0,
      cell: (info) => {
        return <RankIndicator min={minScore} max={maxScore} score={info.getValue() as number} />;
      },
      sortingFn: sortingFns.alphanumeric,
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      accessorFn: (row) => row.scenario.meta?.updatedAt || null,
      cell: ({ row }) => {
        const date = row.original.scenario.meta?.updatedAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: 'edited_at',
      header: 'Last Edited Date',
      accessorFn: (row) => row.scenario.editedAt || null,
      cell: ({ row }) => (
        <TLLink date={row.original.scenario.editedAt}>
          {formatDate(row.original.scenario.editedAt, { formatType: 'datetime' })}
        </TLLink>
      ),
    },
    {
      id: 'created_at',
      header: 'Created Date',
      accessorFn: (row) => row.scenario.meta?.createdAt || null,
      cell: ({ row }) => {
        const date = row.original.scenario.meta?.createdAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: GTInternalIds.tdm,
      header: () => null,
      cell: ({ row }) => {
        return (
          <div css="display: flex;grid-gap: 8px">
            <GTTdm extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original.scenario} />} />
          </div>
        );
      },
      size: 1,
    },
  ];

  return (
    <GenericTable
      tableId={TableIds.rmpScenarios}
      data={data}
      itemHeight={50}
      initialSort={[{ id: '_score', desc: true }]}
      columns={filter(columns, (el) => [...internalIds, ...selectedColumns].includes(el.id as string))}
      rowDisabled={(row) => row.isLinked}
      GTColumnSelectAnchorExported={
        <GTColumnSelectAnchor
          tableId={TableIds.rmpScenarios}
          options={[
            { key: 'assessment_name', title: 'Assessment' },
            { key: 'ux_id', title: 'Id' },
            { key: 'status_scen', title: 'Status' },
            { key: 'name', title: 'Scenario' },
            { key: 'source', title: 'Source' },
            { key: 'event', title: 'Event' },
            { key: 'consequence', title: 'Consequence' },
            {
              key: 'risk_owner',
              title: 'Risk Owner',
            },
            {
              key: 'expected_loss',
              title: 'Expected Loss',
            },
            {
              key: 'risk_identified_date',
              title: 'Identified Date',
            },
            {
              key: 'updated_at',
              title: 'Last Updated',
            },
            {
              key: '_score',
              title: 'Score',
            },
            {
              key: 'edited_at',
              title: 'Last Edited Date',
            },
            {
              key: 'created_at',
              title: 'Created Date',
            },
          ]}
          defaultOptions={defaultColumns}
          value={selectedColumns}
          onChange={setSelectedColumns}
        />
      }
      darkMode
    />
  );
};
